import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Button,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Chip,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";

const Galleries = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    fetchImages();
  }, [baseUrl]);

  const fetchImages = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${baseUrl}/auth/gallery`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setImages(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching gallery images:", error);
      setLoading(false);
    }
  };

  const handleAddImage = () => {
    navigate("/dashboard/addGallery");
  };

  const handleEdit = (id) => {
    navigate(`/dashboard/editGallery/${id}`);
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${baseUrl}/auth/gallery/${id}`,
        {
          isActive: !currentStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      fetchImages();
    } catch (error) {
      console.error("Error toggling image status:", error);
      alert("Failed to update image status");
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this image?",
    );
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${baseUrl}/auth/gallery/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setImages((prevImages) => prevImages.filter((img) => img.id !== id));
      alert("Image deleted successfully");
    } catch (error) {
      console.error("Error deleting image:", error);
      alert("Failed to delete image");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3, mt: 3 }}>
          Gallery Management
        </Typography>
        <Typography>Loading images...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mb: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3, mt: 3 }}>
        Gallery Management
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={handleAddImage}
        style={{ marginBottom: "16px", float: "right" }}
      >
        Add Gallery Image
      </Button>
      <TableContainer component={Paper} style={{ overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Preview</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Display Order</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date Added</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {images.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  sx={{ color: "#000000", textAlign: "center" }}
                >
                  No images found
                </TableCell>
              </TableRow>
            ) : (
              images.map((image) => (
                <TableRow key={image.id}>
                  <TableCell>
                    <img
                      src={`${baseUrl}${image.imageUrl}`}
                      alt={image.title}
                      style={{
                        width: "80px",
                        height: "60px",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/80x60/4B5563/FFFFFF?text=Image";
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{ color: "#000000", fontSize: "1rem", fontWeight: 500 }}
                  >
                    {image.title || "N/A"}
                  </TableCell>
                  <TableCell sx={{ color: "#000000", fontSize: "0.9rem" }}>
                    {image.description
                      ? image.description.substring(0, 50) + "..."
                      : "No description"}
                  </TableCell>
                  <TableCell sx={{ color: "#000000", fontSize: "0.9rem" }}>
                    {image.displayOrder}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={image.isActive}
                      onChange={() =>
                        handleToggleActive(image.id, image.isActive)
                      }
                      color="primary"
                    />
                    <Chip
                      label={image.isActive ? "Active" : "Inactive"}
                      color={image.isActive ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ color: "#000000", fontSize: "0.9rem" }}>
                    {formatDate(image.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(image.id)}
                        title="Edit Image"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(image.id)}
                        title="Delete Image"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default Galleries;
